/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { create } from 'zustand'
import { chunkProject } from '@/lib/rag/chunker'
import { generateEmbeddings, searchSimilar } from '@/lib/rag/embedding-service'
import {
  saveChunks,
  saveEmbeddings,
  getChunks,
  getProjectEmbeddings,
  deleteProjectRAG,
  exportIndex,
  importIndex,
  saveProjectMetadata,
  getAllProjectMetadata,
  type IndexExport,
  type ProjectMetadata
} from '@/lib/rag/rag-storage'
import { smartChatCompletion } from '@/providers/llm-provider'
import { getFileTools, executeFileTool } from '@/providers/file-tools'

export interface RAGMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
  citations?: Array<{
    filePath: string
    startLine: number
    endLine: number
    content: string
  }>
  timestamp: number
}

interface RAGState {
  // State
  messages: RAGMessage[]
  isIndexing: boolean
  isQuerying: boolean
  indexProgress: { current: number; total: number } | null
  error: string | null
  indexed: boolean
  availableIndexes: IndexExport[]
  currentIndexId: string | null

  // Actions
  indexProject: (projectId: string, projectName: string, files: Record<string, string>) => Promise<void>
  askQuestion: (projectId: string, question: string, model: string, files: Record<string, string>, onFileUpdate?: (path: string, content: string) => Promise<void>) => Promise<void>
  clearMessages: () => void
  deleteIndex: (projectId: string) => Promise<void>
  exportCurrentIndex: (projectId: string, projectName: string) => Promise<void>
  importIndexFile: (file: File) => Promise<void>
  loadAvailableIndexes: () => Promise<void>
  loadIndex: (indexData: IndexExport) => Promise<void>
}

export const useRAGStore = create<RAGState>((set, get) => ({
  messages: [],
  isIndexing: false,
  isQuerying: false,
  indexProgress: null,
  error: null,
  indexed: false,
  availableIndexes: [],
  currentIndexId: null,

  indexProject: async (projectId: string, projectName: string, files: Record<string, string>) => {
    set({ isIndexing: true, error: null, indexProgress: null })

    try {
      // Step 1: Chunk the project files
      console.log('[RAG] Chunking project files...')
      const chunks = chunkProject(projectId, files)
      console.log(`[RAG] Created ${chunks.length} chunks`)

      if (chunks.length === 0) {
        throw new Error('No chunks created. Project may be empty or all files are excluded.')
      }

      // Step 2: Save chunks to IndexedDB
      await saveChunks(chunks)

      // Step 3: Generate embeddings with progress
      console.log('[RAG] Generating embeddings...')
      const embeddings = await generateEmbeddings(
        chunks,
        (current, total) => {
          set({ indexProgress: { current, total } })
        }
      )

      // Step 4: Save embeddings to IndexedDB
      await saveEmbeddings(embeddings)

      // Step 5: Save metadata
      const uniqueFiles = new Set(chunks.map(c => c.filePath))
      const now = Date.now()
      const metadata: ProjectMetadata = {
        projectId,
        projectName,
        createdAt: now,
        updatedAt: now,
        totalChunks: chunks.length,
        totalFiles: uniqueFiles.size
      }
      await saveProjectMetadata(metadata)

      console.log(`[RAG] Indexed ${embeddings.length} chunks`)
      set({
        isIndexing: false,
        indexed: true,
        indexProgress: null,
        currentIndexId: projectId,
        messages: [{
          role: 'system',
          content: `✅ Indexed ${embeddings.length} chunks from ${Object.keys(files).length} files. Ready to answer questions!`,
          timestamp: Date.now()
        }]
      })

      // Reload available indexes
      await get().loadAvailableIndexes()
    } catch (error) {
      console.error('[RAG] Indexing failed:', error)
      set({
        isIndexing: false,
        error: (error as Error).message,
        indexProgress: null,
        messages: [{
          role: 'system',
          content: `❌ Indexing failed: ${(error as Error).message}`,
          timestamp: Date.now()
        }]
      })
    }
  },

  askQuestion: async (projectId: string, question: string, model: string, files: Record<string, string>, onFileUpdate?: (path: string, content: string) => Promise<void>) => {
    set({ isQuerying: true, error: null })

    // Add user message
    const userMessage: RAGMessage = {
      role: 'user',
      content: question,
      timestamp: Date.now()
    }
    set({ messages: [...get().messages, userMessage] })

    try {
      // Step 1: Get embeddings for the project
      const embeddings = await getProjectEmbeddings(projectId)

      if (embeddings.length === 0) {
        throw new Error('No embeddings found. Please index the project first.')
      }

      // Step 2: Search for similar chunks
      console.log('[RAG] Searching for relevant chunks...')
      const results = await searchSimilar(question, embeddings, 5)

      // Step 3: Get the actual chunks
      const chunks = await getChunks(projectId)
      const relevantChunks = results.map(r => {
        const chunk = chunks.find(c => c.id === r.embedding.chunkId)
        return { chunk, similarity: r.similarity }
      }).filter(r => r.chunk !== undefined)

      console.log(`[RAG] Found ${relevantChunks.length} relevant chunks`)

      // Step 4: Build context from chunks
      const context = relevantChunks
        .map((r, i) => `[Chunk ${i + 1}] ${r.chunk!.filePath}:${r.chunk!.startLine}-${r.chunk!.endLine}\n${r.chunk!.content}`)
        .join('\n\n---\n\n')

      // Step 5: Generate answer using LLM with file tools
      const prompt = `You are a helpful assistant answering questions about a codebase.

Use the following code chunks to answer the question. Cite specific files and line numbers when relevant.

You have access to file operation tools (create_file, update_file, delete_file, rename_file). Use these tools when the user asks you to create, modify, or delete files.

CONTEXT:
${context}

QUESTION: ${question}

Please provide a clear, concise answer based on the context above. If the context doesn't contain enough information, say so.`

      // Get file tools
      const tools = getFileTools()

      // Use smart routing to automatically select the best provider
      const response = await smartChatCompletion({
        model,
        messages: [{ role: 'user', content: prompt }],
        temperature: 0.3,
        stream: false,
        tools,
        toolChoice: 'auto'
      })

      // Check if the model wants to use tools
      if (response.toolCalls && response.toolCalls.length > 0) {
        console.log('[RAG] Model requested tool calls:', response.toolCalls)

        // Execute each tool call
        for (const toolCall of response.toolCalls) {
          const toolName = toolCall.function.name
          const toolArgs = JSON.parse(toolCall.function.arguments)

          console.log(`[RAG] Executing tool: ${toolName}`, toolArgs)

          try {
            const result = await executeFileTool(toolName, toolArgs, {
              currentProject: { files },
              onConfirmationRequired: async (action) => {
                // Auto-confirm for RAG chat (user explicitly asked for file operations)
                console.log('[RAG] Auto-confirming file operation:', action)
                return true
              },
              onProgress: (message) => {
                console.log('[RAG]', message)
              }
            })

            // If it's a create or update operation, call the onFileUpdate callback
            if ((toolName === 'create_file' || toolName === 'update_file') && onFileUpdate) {
              await onFileUpdate(toolArgs.path, toolArgs.content)
            }

            // Add tool result message
            const toolResultMessage: RAGMessage = {
              role: 'system',
              content: `✓ ${result}`,
              timestamp: Date.now()
            }
            set({ messages: [...get().messages, toolResultMessage] })

          } catch (error) {
            console.error(`[RAG] Tool execution failed:`, error)
            const errorMsg: RAGMessage = {
              role: 'system',
              content: `✗ Tool execution failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
              timestamp: Date.now()
            }
            set({ messages: [...get().messages, errorMsg] })
          }
        }
      }

      // Step 6: Add assistant message with citations
      const assistantMessage: RAGMessage = {
        role: 'assistant',
        content: response.content || 'File operation completed.',
        citations: relevantChunks.map(r => ({
          filePath: r.chunk!.filePath,
          startLine: r.chunk!.startLine,
          endLine: r.chunk!.endLine,
          content: r.chunk!.content.substring(0, 200) + '...'
        })),
        timestamp: Date.now()
      }

      set({
        messages: [...get().messages, assistantMessage],
        isQuerying: false
      })
    } catch (error) {
      console.error('[RAG] Query failed:', error)
      const errorMessage: RAGMessage = {
        role: 'system',
        content: `❌ Error: ${(error as Error).message}`,
        timestamp: Date.now()
      }
      set({
        messages: [...get().messages, errorMessage],
        isQuerying: false,
        error: (error as Error).message
      })
    }
  },

  clearMessages: () => {
    set({ messages: [], error: null })
  },

  deleteIndex: async (projectId: string) => {
    try {
      await deleteProjectRAG(projectId)
      set({ indexed: false, messages: [], error: null, currentIndexId: null })
      await get().loadAvailableIndexes()
      console.log('[RAG] Index deleted')
    } catch (error) {
      console.error('[RAG] Failed to delete index:', error)
      set({ error: (error as Error).message })
    }
  },

  exportCurrentIndex: async (projectId: string, projectName: string) => {
    try {
      const indexData = await exportIndex(projectId, projectName)

      // Download as JSON file
      const blob = new Blob([JSON.stringify(indexData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${projectName}-index-${Date.now()}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      console.log('[RAG] Index exported successfully')
    } catch (error) {
      console.error('[RAG] Failed to export index:', error)
      set({ error: (error as Error).message })
    }
  },

  importIndexFile: async (file: File) => {
    try {
      const text = await file.text()
      const indexData = JSON.parse(text) as IndexExport

      await importIndex(indexData)

      set({
        indexed: true,
        currentIndexId: indexData.projectId,
        messages: [{
          role: 'system',
          content: `✅ Imported index: ${indexData.projectName} (${indexData.metadata.totalChunks} chunks from ${indexData.metadata.totalFiles} files)`,
          timestamp: Date.now()
        }]
      })

      await get().loadAvailableIndexes()
      console.log('[RAG] Index imported successfully')
    } catch (error) {
      console.error('[RAG] Failed to import index:', error)
      set({
        error: (error as Error).message,
        messages: [{
          role: 'system',
          content: `❌ Failed to import index: ${(error as Error).message}`,
          timestamp: Date.now()
        }]
      })
    }
  },

  loadAvailableIndexes: async () => {
    try {
      // Load all project metadata (sorted by updatedAt, newest first)
      const allMetadata = await getAllProjectMetadata()
      const indexes: IndexExport[] = []

      for (const metadata of allMetadata) {
        const chunks = await getChunks(metadata.projectId)
        const embeddings = await getProjectEmbeddings(metadata.projectId)

        if (chunks.length > 0) {
          indexes.push({
            version: 1,
            projectId: metadata.projectId,
            projectName: metadata.projectName,
            createdAt: metadata.createdAt,
            chunks,
            embeddings,
            metadata: {
              totalChunks: metadata.totalChunks,
              totalFiles: metadata.totalFiles,
              embeddingModel: embeddings[0]?.model || 'text-embedding-3-small'
            }
          })
        }
      }

      set({ availableIndexes: indexes })
    } catch (error) {
      console.error('[RAG] Failed to load available indexes:', error)
    }
  },

  loadIndex: async (indexData: IndexExport) => {
    try {
      set({
        indexed: true,
        currentIndexId: indexData.projectId,
        messages: [{
          role: 'system',
          content: `✅ Loaded index: ${indexData.projectName} (${indexData.metadata.totalChunks} chunks from ${indexData.metadata.totalFiles} files)`,
          timestamp: Date.now()
        }]
      })

      console.log('[RAG] Index loaded successfully')
    } catch (error) {
      console.error('[RAG] Failed to load index:', error)
      set({ error: (error as Error).message })
    }
  }
}))

